import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Cost, CopyButton, PageHeader, Section, Spinner } from "../components/AiHtml";
import { useGenerate } from "../lib/useGenerate";

const PLATFORMS = [["google", "Google Ads"], ["meta", "Meta Ads"], ["linkedin", "LinkedIn Ads"], ["chatgpt", "ChatGPT Ads"]];
const compColor = { Low: "text-emerald-400 bg-emerald-500/10", Medium: "text-amber-400 bg-amber-500/10", High: "text-red-400 bg-red-500/10" };

const List = ({ title, items }) => (
  <Section title={title} right={<CopyButton text={(items || []).join("\n")} />}>
    <ul className="space-y-2">{(items || []).map((x, i) => <li key={i} className="text-sm text-slate-200 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2">{x}</li>)}</ul>
  </Section>
);

export default function AdsManager() {
  const [platform, setPlatform] = useState("google");
  const [prompt, setPrompt] = useState("");
  const [budget, setBudget] = useState("");
  const g = useGenerate("adpack");
  const r = g.result;
  const label = PLATFORMS.find((p) => p[0] === platform)[1];

  return (
    <div className="space-y-6">
      <PageHeader label="Ads Manager" title="Ad packs + keyword research" desc="Platform-native ad copy, audiences and keyword estimates with volume, CPC and competition." />
      <Tabs value={platform} onValueChange={(v) => { setPlatform(v); g.setResult(null); }}>
        <TabsList className="bg-slate-900 border border-slate-800 w-full sm:w-auto justify-start overflow-x-auto scroll-thin">
          {PLATFORMS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`ads-tab-${v}`} className="shrink-0 data-[state=active]:bg-orange-500 data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="card-n p-5 space-y-4">
        <Textarea data-testid="ads-prompt-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={`What should this ${label} campaign promote?`} />
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Input data-testid="ads-budget-input" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Monthly budget (e.g. Rs 50,000)" className="sm:max-w-xs" />
          <span className="self-start sm:self-auto"><Cost n={12} /></span>
          <Button data-testid="ads-generate-btn" disabled={g.busy || !prompt.trim()} onClick={() => g.run(prompt, { platform: label, budget })} className="rounded-full sm:ml-auto"><Sparkles size={16} className="mr-1.5" /> Generate ad pack</Button>
        </div>
      </div>
      {g.busy && <div className="card-n"><Spinner label={`Building your ${label} ad pack...`} /></div>}
      {!g.busy && r && (
        <div className="space-y-6" data-testid="ads-result">
          <Section title={r.campaign_name}><p className="text-sm text-slate-400">{r.objective}</p>{r.budget_tip && <p className="text-sm text-orange-300 mt-2">{r.budget_tip}</p>}</Section>
          <Section title="Keyword research" testId="ads-keyword-table">
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[480px]"><thead><tr className="text-left text-xs text-slate-500 font-mono uppercase"><th className="py-2">Keyword</th><th>Volume</th><th>CPC</th><th>Competition</th></tr></thead>
              <tbody>{(r.keywords || []).map((k, i) => <tr key={i} className="border-t border-slate-800"><td className="py-2.5 text-slate-100">{k.keyword}</td><td className="font-mono">{Number(k.volume).toLocaleString()}</td><td className="font-mono">{k.cpc}</td><td><span className={`px-2 py-0.5 rounded-full text-xs ${compColor[k.competition] || "text-slate-300"}`}>{k.competition}</span></td></tr>)}</tbody></table></div>
          </Section>
          <div className="grid lg:grid-cols-2 gap-6">
            <List title="Headlines" items={r.headlines} />
            <List title="Descriptions" items={r.descriptions} />
            <List title="Primary texts" items={r.primary_texts} />
            <div className="space-y-6"><List title="Audiences" items={r.audiences} /><List title="CTAs" items={r.ctas} /></div>
          </div>
        </div>
      )}
    </div>
  );
}
